import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import axios, { useApiRequest } from "../../../api/axios";
import { useCRUD } from "../../../api/crud";
import {
  EQUIPMENT_COUNTERS_BULK_ENDPOINT,
  EQUIPMENT_COUNTERS_ENDPOINT,
  EQUIPMENTS_ENDPOINT,
  ERROR_SNACKBAR,
  JOBS_ENDPOINT,
  SUCCESS_SNACKBAR,
} from "../../../consts";
import { formatCounterInputValue, formatCounterValue, normalizeCounterInputValue } from "../../../utils";
import { useHasPermission } from "../../../contexts/auth";
import AnimatedHelperText from "../animatedHelperText/animatedHelperText.component";
import CustomCircularProgress from "../customCircularProgress/customCircularProgress.component";
import CustomDateTimePicker from "../customDateTimePicker/customDateTimePicker.component";
import { DATE_TIME_PICKER_FORMAT } from "../datePickerFormats";
import styles from "../dynamicForm/dynamicForm.module.css";
import {
  CAN_ENTER_EVERY_COUNTER_VALUE_PERMISSION,
  LowCounterValueConfirmModal,
  LowCounterValueWarning,
  parseCounterNumericValue,
} from "./lowCounterValueWarning.component";

type HiddenCounterField = "equipment_id" | "job" | "kind" | "provided_by_client" | "read_at";

type EquipmentOption = {
  id: number | string;
  full_name?: string;
  serial?: string;
  kind?: string;
};

type JobOption = {
  id: number | string;
  number?: string | null;
};

export type LastCounterData = {
  value: number;
  date: string;
  kind: string;
};

type EquipmentCounterApiRow = {
  value: number | null;
  read_at: string;
  kind: string;
};

type CounterFormData = Record<string, unknown>;

type CounterRowState = {
  key: number;
  data: CounterFormData;
};

const COUNTER_KIND_CHOICES = [
  { value: "BP", label: "Black pages" },
  { value: "CP", label: "Color pages" },
  { value: "SP", label: "Scanned pages" },
] as const;

const BLACK_ONLY_EQUIPMENT_KIND = "B";

function getAvailableCounterKindChoices(equipmentKind?: string) {
  if (equipmentKind !== BLACK_ONLY_EQUIPMENT_KIND) {
    return COUNTER_KIND_CHOICES;
  }
  // Black-only equipment cannot produce color page counts.
  return COUNTER_KIND_CHOICES.filter((choice) => choice.value !== "CP");
}

type RawLowCounterValueWarning = {
  kind: string;
  enteredValue: number;
  latestValue: number;
  latestDate: string;
};

export interface EquipmentCounterFormProps {
  onSuccess?: (data?: any) => void;
  id?: string;
  equipmentId?: string | number | null;
  jobId?: string | number | null;
  initialData?: Record<string, unknown>;
  equipmentOptionsOverride?: EquipmentOption[];
  hiddenFields?: HiddenCounterField[];
  hideSubmit?: boolean;
  hidePreviousCountersTable?: boolean;
  hideLastValueHint?: boolean;
  wideFields?: boolean;
  onFormDataChange?: (data: Record<string, unknown>) => void;
}

const pruneEmptyStrings = (value: unknown): unknown => {
  if (value === null) return value;
  if (value === "") return undefined;

  if (Array.isArray(value)) {
    const cleanedArray = value
      .map((item) => pruneEmptyStrings(item))
      .filter((item) => item !== undefined);
    return cleanedArray.length > 0 ? cleanedArray : undefined;
  }

  if (value && typeof value === "object") {
    const cleanedObject = Object.entries(value as Record<string, unknown>).reduce(
      (acc, [key, entryValue]) => {
        const cleanedValue = pruneEmptyStrings(entryValue);
        if (cleanedValue !== undefined) {
          acc[key] = cleanedValue;
        }
        return acc;
      },
      {} as Record<string, unknown>
    );
    return Object.keys(cleanedObject).length > 0 ? cleanedObject : undefined;
  }

  return value;
};

const normalizeId = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  return String(value);
};

const fieldBoxSx = {
  flex: { xs: "1 1 100%", sm: "1 1 calc((100% - 32px) / 3)" },
  maxWidth: { xs: "100%", sm: "calc((100% - 32px) / 3)" },
};

const wideFieldBoxSx = {
  flex: { xs: "1 1 100%", sm: "1 1 190px" },
  maxWidth: { xs: "100%", sm: "none" },
};

const getCounterKindLabel = (kind: string) =>
  COUNTER_KIND_CHOICES.find((choice) => choice.value === kind)?.label ?? kind;

const collectLowCounterValueWarnings = (
  entries: { formData: CounterFormData; lastCounters: LastCounterData[] }[]
): RawLowCounterValueWarning[] => {
  const warnings: RawLowCounterValueWarning[] = [];

  for (const { formData, lastCounters } of entries) {
    const kind = formData.kind;
    if (typeof kind !== "string" || !kind) {
      continue;
    }

    const numericValue = parseCounterNumericValue(formData.value);
    if (numericValue === null) {
      continue;
    }

    const latest = lastCounters.find((counter) => counter.kind === kind);
    if (!latest || numericValue >= latest.value) {
      continue;
    }

    warnings.push({
      kind,
      enteredValue: numericValue,
      latestValue: latest.value,
      latestDate: latest.date,
    });
  }

  return warnings;
};

// Canonical display order for counter kinds - Black pages, then Color pages,
// then Scanned pages, regardless of the order they come back from the API
// (which is sorted by read date, not kind).
const COUNTER_KIND_ORDER: Record<string, number> = { BP: 0, CP: 1, SP: 2 };
const sortByCounterKind = (counters: LastCounterData[]) =>
  [...counters].sort(
    (a, b) => (COUNTER_KIND_ORDER[a.kind] ?? 99) - (COUNTER_KIND_ORDER[b.kind] ?? 99)
  );

export function useEquipmentLastCounters(equipmentId?: unknown): LastCounterData[] {
  const { executeRequest } = useApiRequest();
  const [lastCountersData, setLastCountersData] = useState<LastCounterData[]>([]);

  useEffect(() => {
    if (!equipmentId) {
      setLastCountersData([]);
      return;
    }

    let isCancelled = false;

    executeRequest(() =>
      axios.get(
        `${EQUIPMENT_COUNTERS_ENDPOINT}?equipment=${equipmentId}&ordering=-read_at&page_size=10`
      )
    ).then((result) => {
      if (isCancelled) return;

      const counters: EquipmentCounterApiRow[] = Array.isArray(result?.data?.results)
        ? result.data.results
        : [];
      const latestByKind = counters.reduce((acc: LastCounterData[], counter: EquipmentCounterApiRow) => {
        if (counter?.value == null || !counter?.kind) {
          return acc;
        }

        if (acc.some((item) => item.kind === counter.kind)) {
          return acc;
        }

        acc.push({
          kind: counter.kind,
          value: Number(counter.value),
          date: dayjs(counter.read_at).format(DATE_TIME_PICKER_FORMAT),
        });
        return acc;
      }, []);

      setLastCountersData(sortByCounterKind(latestByKind));
    });

    return () => {
      isCancelled = true;
    };
  }, [equipmentId, executeRequest]);

  return lastCountersData;
}

const buildDefaultValues = ({
  equipmentId,
  jobId,
  initialData,
}: Pick<EquipmentCounterFormProps, "equipmentId" | "jobId" | "initialData">): CounterFormData => {
  const defaults: CounterFormData = { ...(initialData || {}) };

  if (equipmentId !== undefined) {
    defaults.equipment_id = equipmentId;
  }

  if (jobId !== undefined) {
    defaults.job = jobId;
  }

  if (
    defaults.provider === undefined
    || defaults.provider === null
    || defaults.provider === ""
  ) {
    defaults.provider = "U";
  }

  if (!defaults.read_at) {
    defaults.read_at = dayjs().format("YYYY-MM-DDTHH:mm:ss");
  }

  return defaults;
};

const buildCounterPayload = (
  formData: CounterFormData,
  verify = false
): Record<string, unknown> => {
  const payload: Record<string, unknown> = {
    equipment_id: formData.equipment_id ?? null,
    job: formData.job ?? null,
    kind: formData.kind ?? "",
    provider: formData.provider ?? null,
    value: formData.value ?? null,
    read_at: formData.read_at ?? null,
  };

  if (verify) {
    payload.verify = true;
  }

  return (pruneEmptyStrings(payload) ?? {}) as Record<string, unknown>;
};

const hasFilledCounterRow = (formData: CounterFormData): boolean =>
  formData.value !== null
  && formData.value !== undefined
  && formData.value !== ""
  && Boolean(formData.kind);

const getFieldErrorMessage = (fieldErrors: any, fieldName: string) => {
  const value = fieldErrors?.[fieldName];

  if (!Array.isArray(value) || value.length === 0) {
    return null;
  }

  const firstError = value[0];
  if (typeof firstError === "string") {
    return firstError;
  }

  return firstError?.message ?? null;
};

const getRequestErrorMessage = (
  requestError: any,
  fallbackMessage: string
): string => {
  if (typeof requestError === "string") {
    return requestError;
  }

  if (typeof requestError?.message === "string") {
    return requestError.message;
  }

  if (typeof requestError?.detail === "string") {
    return requestError.detail;
  }

  return fallbackMessage;
};

interface EquipmentCounterRowFieldsProps {
  formData: CounterFormData;
  onChange: (data: CounterFormData) => void;
  hiddenFields: HiddenCounterField[];
  equipmentOptionsOverride?: EquipmentOption[];
  fieldErrors?: any;
  onLastCountersChange?: (data: LastCounterData[]) => void;
  hideLastValueHint?: boolean;
  wideFields?: boolean;
}

export function PreviousCountersTable({ equipmentId }: { equipmentId?: unknown }): ReactElement | null {
  const { t } = useTranslation();
  const lastCountersData = useEquipmentLastCounters(equipmentId);

  if (lastCountersData.length === 0) {
    return null;
  }

  return (
    <Box
      component="fieldset"
      sx={{
        width: "100%",
        m: 0,
        px: 2,
        pb: 2,
        borderColor: "divider",
        borderRadius: 1,
        borderWidth: 1,
      }}
    >
      <Box
        component="legend"
        sx={{ px: 1, color: "text.secondary", fontSize: "0.875rem" }}
      >
        {t("Previous counter values")}
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("Kind")}</TableCell>
              <TableCell>{t("Value")}</TableCell>
              <TableCell>{t("Date")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lastCountersData.map((counter) => (
              <TableRow key={counter.kind}>
                <TableCell>{t(getCounterKindLabel(counter.kind))}</TableCell>
                <TableCell>{formatCounterValue(counter.value)}</TableCell>
                <TableCell>{counter.date}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function EquipmentCounterRowFields({
  formData,
  onChange,
  hiddenFields,
  equipmentOptionsOverride,
  fieldErrors,
  onLastCountersChange,
  hideLastValueHint = false,
  wideFields = false,
}: EquipmentCounterRowFieldsProps): ReactElement {
  const { t } = useTranslation();
  const { executeRequest } = useApiRequest();
  const [equipmentOptions, setEquipmentOptions] = useState<EquipmentOption[]>([]);
  const [jobOptions, setJobOptions] = useState<JobOption[]>([]);
  const [equipmentLoading, setEquipmentLoading] = useState(false);
  const [jobLoading, setJobLoading] = useState(false);
  const lastCountersData = useEquipmentLastCounters(formData.equipment_id);

  useEffect(() => {
    onLastCountersChange?.(lastCountersData);
  }, [lastCountersData, onLastCountersChange]);

  const isHidden = useCallback(
    (fieldName: HiddenCounterField) => hiddenFields.includes(fieldName),
    [hiddenFields]
  );

  const setField = useCallback(
    (fieldName: string, value: unknown) => {
      onChange({ ...formData, [fieldName]: value });
    },
    [formData, onChange]
  );

  const fetchEquipmentOptions = useCallback(
    async (search?: string) => {
      if (equipmentOptionsOverride) return;

      setEquipmentLoading(true);
      try {
        const params = new URLSearchParams();
        if (search && search.trim().length >= 2) {
          params.set("search", search.trim());
        }

        const { data } = await executeRequest(() =>
          axios.get(
            `${EQUIPMENTS_ENDPOINT}${params.toString() ? `?${params.toString()}` : ""}`
          )
        );
        const nextOptions = Array.isArray(data?.results) ? data.results : data || [];
        setEquipmentOptions(nextOptions);
      } catch {
        setEquipmentOptions([]);
      } finally {
        setEquipmentLoading(false);
      }
    },
    [equipmentOptionsOverride, executeRequest]
  );

  const fetchJobOptions = useCallback(
    async (search?: string) => {
      setJobLoading(true);
      try {
        const params = new URLSearchParams();
        if (search && search.trim().length >= 2) {
          params.set("search", search.trim());
        }

        const { data } = await executeRequest(() =>
          axios.get(`${JOBS_ENDPOINT}${params.toString() ? `?${params.toString()}` : ""}`)
        );
        const nextOptions = Array.isArray(data?.results) ? data.results : data || [];
        setJobOptions(nextOptions);
      } catch {
        setJobOptions([]);
      } finally {
        setJobLoading(false);
      }
    },
    [executeRequest]
  );

  useEffect(() => {
    if (equipmentOptionsOverride || isHidden("equipment_id")) return;

    const selectedEquipmentId = formData.equipment_id;
    if (!selectedEquipmentId) return;

    const hasSelectedEquipment = equipmentOptions.some(
      (option) => normalizeId(option.id) === normalizeId(selectedEquipmentId)
    );
    if (hasSelectedEquipment) return;

    executeRequest(() => axios.get(`${EQUIPMENTS_ENDPOINT}${selectedEquipmentId}/`)).then((result) => {
      if (result?.data) {
        setEquipmentOptions((prev) => {
          if (prev.some((option) => normalizeId(option.id) === normalizeId(result.data.id))) {
            return prev;
          }
          return [...prev, result.data];
        });
      }
    });
  }, [equipmentOptions, equipmentOptionsOverride, executeRequest, formData.equipment_id, isHidden]);

  useEffect(() => {
    if (isHidden("job")) return;

    const selectedJobId = formData.job;
    if (!selectedJobId) return;

    const hasSelectedJob = jobOptions.some(
      (option) => normalizeId(option.id) === normalizeId(selectedJobId)
    );
    if (hasSelectedJob) return;

    executeRequest(() => axios.get(`${JOBS_ENDPOINT}${selectedJobId}/`)).then((result) => {
      if (result?.data) {
        setJobOptions((prev) => {
          if (prev.some((option) => normalizeId(option.id) === normalizeId(result.data.id))) {
            return prev;
          }
          return [...prev, result.data];
        });
      }
    });
  }, [executeRequest, formData.job, isHidden, jobOptions]);

  const currentEquipmentOptions = equipmentOptionsOverride ?? equipmentOptions;
  const selectedEquipment =
    currentEquipmentOptions.find(
      (option) => normalizeId(option.id) === normalizeId(formData.equipment_id)
    ) ?? null;
  const selectedJob =
    jobOptions.find((option) => normalizeId(option.id) === normalizeId(formData.job)) ?? null;
  const selectedLastCounter = lastCountersData.find(
    (counter) => counter.kind === formData.kind
  );
  const lastCounterHelperText = selectedLastCounter
    && `${t("last")}: ${formatCounterValue(selectedLastCounter.value)} (${selectedLastCounter.date})`;
  const availableKindChoices = getAvailableCounterKindChoices(selectedEquipment?.kind);

  useEffect(() => {
    if (isHidden("kind") || formData.kind) return;
    if (availableKindChoices.length === 1) {
      setField("kind", availableKindChoices[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableKindChoices, formData.kind, isHidden]);

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
      {!isHidden("equipment_id") && (
        <Box sx={fieldBoxSx}>
          <Autocomplete
            size="small"
            options={currentEquipmentOptions}
            value={selectedEquipment}
            loading={!equipmentOptionsOverride && equipmentLoading}
            getOptionLabel={(option) =>
              typeof option === "string"
                ? option
                : option.full_name || option.serial || String(option.id)
            }
            isOptionEqualToValue={(option, value) => normalizeId(option?.id) === normalizeId(value?.id)}
            onOpen={() => fetchEquipmentOptions()}
            onInputChange={(_, value, reason) => {
              if (equipmentOptionsOverride || reason !== "input") return;
              if (!value) {
                fetchEquipmentOptions();
                return;
              }
              fetchEquipmentOptions(value);
            }}
            onChange={(_, newValue) => setField("equipment_id", newValue?.id ?? null)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("Equipment")}
                required
                fullWidth
              />
            )}
            noOptionsText={t("No options")}
          />
          {getFieldErrorMessage(fieldErrors, "equipment_id") && (
            <div className={styles.errorMessage}>{getFieldErrorMessage(fieldErrors, "equipment_id")}</div>
          )}
        </Box>
      )}

      {!isHidden("kind") && (
        <Box sx={fieldBoxSx}>
          <FormControl fullWidth size="small" required>
            <InputLabel>{t("Kind")}</InputLabel>
            <Select
              label={t("Kind")}
              value={(formData.kind as string) ?? ""}
              onChange={(event) => setField("kind", event.target.value)}
            >
              {availableKindChoices.map((choice) => (
                <MenuItem key={choice.value} value={choice.value}>
                  {t(choice.label)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {getFieldErrorMessage(fieldErrors, "kind") && (
            <div className={styles.errorMessage}>{getFieldErrorMessage(fieldErrors, "kind")}</div>
          )}
        </Box>
      )}

      <Box sx={wideFields ? wideFieldBoxSx : fieldBoxSx}>
        <TextField
          size="small"
          label={t("Value")}
          helperText={
            !hideLastValueHint && (
              <AnimatedHelperText visible={!!selectedLastCounter}>
                {lastCounterHelperText}
              </AnimatedHelperText>
            )
          }
          value={
            formData.value === null || formData.value === undefined
              ? ""
              : formatCounterInputValue(String(formData.value))
          }
          required
          onChange={(event) => {
            const nextValue = event.target.value;

            if (nextValue === "") {
              setField("value", null);
              return;
            }

            const normalizedValue = normalizeCounterInputValue(nextValue);
            if (normalizedValue === null) {
              return;
            }

            setField("value", normalizedValue);
          }}
          fullWidth
          type="text"
          inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
        />
        {getFieldErrorMessage(fieldErrors, "value") && (
          <div className={styles.errorMessage}>{getFieldErrorMessage(fieldErrors, "value")}</div>
        )}
      </Box>

      {!isHidden("job") && (
        <Box sx={fieldBoxSx}>
          <Autocomplete
            size="small"
            options={jobOptions}
            value={selectedJob}
            loading={jobLoading}
            getOptionLabel={(option) =>
              typeof option === "string"
                ? option
                : option.number || String(option.id)
            }
            isOptionEqualToValue={(option, value) => normalizeId(option?.id) === normalizeId(value?.id)}
            onOpen={() => fetchJobOptions()}
            onInputChange={(_, value, reason) => {
              if (reason !== "input") return;
              if (!value) {
                fetchJobOptions();
                return;
              }
              fetchJobOptions(value);
            }}
            onChange={(_, newValue) => setField("job", newValue?.id ?? null)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("Job")}
                fullWidth
              />
            )}
            noOptionsText={t("No options")}
          />
          {getFieldErrorMessage(fieldErrors, "job") && (
            <div className={styles.errorMessage}>{getFieldErrorMessage(fieldErrors, "job")}</div>
          )}
        </Box>
      )}

      {!isHidden("read_at") && (
        <Box sx={wideFields ? wideFieldBoxSx : fieldBoxSx}>
          <CustomDateTimePicker
            label={t("Read at")}
            value={formData.read_at ? dayjs(formData.read_at as string) : null}
            onChange={(value: Dayjs | null) =>
              setField("read_at", value ? value.format("YYYY-MM-DDTHH:mm:ss") : null)
            }
            ampm={false}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                required: true,
              },
            }}
          />
          {getFieldErrorMessage(fieldErrors, "read_at") && (
            <div className={styles.errorMessage}>{getFieldErrorMessage(fieldErrors, "read_at")}</div>
          )}
        </Box>
      )}
      {!isHidden("provided_by_client") && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            alignSelf: { sm: "flex-end" },
            flex: { xs: "1 1 100%", sm: "0 0 auto" },
            maxWidth: { xs: "100%", sm: "none" },
            pb: { sm: 0.5 },
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={(formData.provider as string) === "C"}
                onChange={(_, checked) => setField("provider", checked ? "C" : "U")}
              />
            }
            label={t("Provided by client")}
            sx={{ m: 0 }}
          />
          {getFieldErrorMessage(fieldErrors, "provider") && (
            <div className={styles.errorMessage}>{getFieldErrorMessage(fieldErrors, "provider")}</div>
          )}
        </Box>
      )}
    </Box>
  );
}

export default function EquipmentCounterForm({
  onSuccess,
  id,
  equipmentId,
  jobId,
  initialData,
  equipmentOptionsOverride,
  hiddenFields = [],
  hideSubmit = false,
  hidePreviousCountersTable = false,
  hideLastValueHint = false,
  wideFields = false,
  onFormDataChange,
}: EquipmentCounterFormProps): ReactElement {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const canEnterEveryCounterValue = useHasPermission(CAN_ENTER_EVERY_COUNTER_VALUE_PERMISSION);
  const { get, create, update, loading, error } = useCRUD(EQUIPMENT_COUNTERS_ENDPOINT);
  const {
    executeRequest: executeBulkRequest,
    loading: bulkLoading,
    error: bulkError,
  } = useApiRequest();
  const [formData, setFormData] = useState<CounterFormData>({});
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);
  const [rows, setRows] = useState<CounterRowState[]>([]);
  const [lowValueConfirmOpen, setLowValueConfirmOpen] = useState(false);
  const [lowValueWarnings, setLowValueWarnings] = useState<LowCounterValueWarning[]>([]);
  const [pendingVerify, setPendingVerify] = useState(false);
  const initializedWithoutIdRef = useRef(false);
  const lastEmittedFormDataRef = useRef<string>("");
  const nextRowKeyRef = useRef(0);
  const rowLastCountersRef = useRef<Record<number, LastCounterData[]>>({});
  const isMultiCreateMode = !id && !hideSubmit && !onFormDataChange;
  const singleModeLastCounters = useEquipmentLastCounters(formData.equipment_id);

  const getDefaultValues = useCallback(
    () =>
      buildDefaultValues({
        equipmentId,
        jobId,
        initialData,
      }),
    [equipmentId, initialData, jobId]
  );

  const createRowState = useCallback(
    () => ({
      key: nextRowKeyRef.current++,
      data: getDefaultValues(),
    }),
    [getDefaultValues]
  );

  useEffect(() => {
    if (!isMultiCreateMode) return;

    setRows((prev) => (prev.length > 0 ? prev : [createRowState()]));
  }, [createRowState, isMultiCreateMode]);

  useEffect(() => {
    if (isMultiCreateMode) {
      setIsLoadingInitial(false);
      return;
    }

    const defaultValues = getDefaultValues();

    if (!id) {
      if (!initializedWithoutIdRef.current) {
        setFormData(defaultValues);
        initializedWithoutIdRef.current = true;
      }
      setIsLoadingInitial(false);
      return;
    }

    get(id)
      .then((response) => {
        if (!response?.data) {
          setFormData(defaultValues);
          setIsLoadingInitial(false);
          return;
        }

        setFormData({
          ...defaultValues,
          ...response.data,
        });
        setIsLoadingInitial(false);
      })
      .catch(() => {
        setFormData(defaultValues);
        setIsLoadingInitial(false);
      });
  }, [get, getDefaultValues, id, isMultiCreateMode]);

  useEffect(() => {
    if (isMultiCreateMode) return;
    if (isLoadingInitial) return;
    const serializedFormData = JSON.stringify(formData);
    if (serializedFormData === lastEmittedFormDataRef.current) return;
    lastEmittedFormDataRef.current = serializedFormData;
    onFormDataChange?.(formData);
  }, [formData, isLoadingInitial, isMultiCreateMode, onFormDataChange]);

  const handleSingleRowChange = useCallback((data: CounterFormData) => {
    setFormData(data);
  }, []);

  const handleRowChange = useCallback((key: number, data: CounterFormData) => {
    setRows((prev) => prev.map((row) => (row.key === key ? { ...row, data } : row)));
  }, []);

  const handleAddRow = useCallback(() => {
    setRows((prev) => [...prev, createRowState()]);
  }, [createRowState]);

  const handleRemoveRow = useCallback((key: number) => {
    setRows((prev) => (prev.length === 1 ? prev : prev.filter((row) => row.key !== key)));
  }, []);

  const performSubmit = async (verify = false) => {
    if (isMultiCreateMode) {
      const counters = rows
        .map((row) => buildCounterPayload(row.data, verify))
        .filter(hasFilledCounterRow);

      if (counters.length === 0) {
        enqueueSnackbar(t("Please fill at least one counter."), ERROR_SNACKBAR);
        return;
      }

      const result = await executeBulkRequest(() =>
        axios.post(EQUIPMENT_COUNTERS_BULK_ENDPOINT, { counters })
      );

      if (result?.success) {
        enqueueSnackbar(t("Form was succesfully processed."), SUCCESS_SNACKBAR);
        onSuccess?.(result.data);
        return;
      }

      enqueueSnackbar(
        getRequestErrorMessage(
          result?.errorData,
          t("There was an error, please review the form.")
        ),
        ERROR_SNACKBAR
      );
      return;
    }

    const result = id
      ? await update({ id, ...buildCounterPayload(formData, verify) })
      : await create(buildCounterPayload(formData, verify));

    if (result?.success) {
      enqueueSnackbar(t("Form was succesfully processed."), SUCCESS_SNACKBAR);
      onSuccess?.(result.data);
      return;
    }

    const failureMessage =
      (typeof result?.errorData === "string"
        ? result.errorData
        : result?.errorData?.message) ||
      (typeof result?.data === "string" ? result.data : result?.data?.message) ||
      getRequestErrorMessage(error, t("There was an error, please review the form.")) ||
      t("There was an error, please review the form.");

    enqueueSnackbar(failureMessage, ERROR_SNACKBAR);
  };

  const requestSubmit = (verify = false) => {
    if (canEnterEveryCounterValue) {
      const entries = isMultiCreateMode
        ? rows
            .filter((row) => hasFilledCounterRow(row.data))
            .map((row) => ({
              formData: row.data,
              lastCounters: rowLastCountersRef.current[row.key] ?? [],
            }))
        : [{ formData, lastCounters: singleModeLastCounters }];

      const warnings = collectLowCounterValueWarnings(entries);
      if (warnings.length > 0) {
        setLowValueWarnings(
          warnings.map((warning) => ({
            key: warning.kind,
            label: t(getCounterKindLabel(warning.kind)),
            enteredValue: warning.enteredValue,
            latestValue: warning.latestValue,
            latestDate: warning.latestDate,
          }))
        );
        setPendingVerify(verify);
        setLowValueConfirmOpen(true);
        return;
      }
    }

    void performSubmit(verify);
  };

  const handleConfirmLowValueSubmit = () => {
    if (!canEnterEveryCounterValue) {
      setLowValueConfirmOpen(false);
      return;
    }

    setLowValueConfirmOpen(false);
    void performSubmit(pendingVerify);
  };

  const lowValueConfirmModal = canEnterEveryCounterValue ? (
    <LowCounterValueConfirmModal
      opened={lowValueConfirmOpen}
      setOpened={setLowValueConfirmOpen}
      warnings={lowValueWarnings}
      onConfirm={handleConfirmLowValueSubmit}
    />
  ) : null;

  if (isLoadingInitial) {
    return <CustomCircularProgress />;
  }

  const multiCreateEquipmentIds = rows.reduce<string[]>((acc, row) => {
    const normalizedEquipmentId = normalizeId(row.data.equipment_id);

    if (!normalizedEquipmentId || acc.includes(normalizedEquipmentId)) {
      return acc;
    }

    return [...acc, normalizedEquipmentId];
  }, []);
  const sharedEquipmentId = isMultiCreateMode
    ? (multiCreateEquipmentIds.length === 1 ? multiCreateEquipmentIds[0] : undefined)
    : formData.equipment_id;
  const showVerifySubmit = !id;

  if (isMultiCreateMode) {
    const rowErrors = Array.isArray(bulkError?.counters) ? bulkError.counters : [];

    return (
      <>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {!hidePreviousCountersTable && (
            <PreviousCountersTable equipmentId={sharedEquipmentId} />
          )}

          {rows.map((row, index) => (
          <Box
            key={row.key}
            sx={{
              border: "1px solid #eee",
              borderRadius: 1,
              p: 2,
              backgroundColor: "var(--componentBgColor)",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography variant="subtitle2" sx={{ color: "var(--textColor)" }}>
                {t("Counter")} #{index + 1}
              </Typography>
              <IconButton
                size="small"
                color="error"
                onClick={() => handleRemoveRow(row.key)}
                disabled={rows.length === 1}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Box>

            <EquipmentCounterRowFields
              formData={row.data}
              onChange={(data) => handleRowChange(row.key, data)}
              hiddenFields={hiddenFields}
              equipmentOptionsOverride={equipmentOptionsOverride}
              fieldErrors={rowErrors[index]}
              hideLastValueHint={hideLastValueHint}
              wideFields={wideFields}
              onLastCountersChange={(data) => {
                rowLastCountersRef.current[row.key] = data;
              }}
            />
          </Box>
        ))}

          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
            <Button startIcon={<Add />} variant="outlined" size="small" onClick={handleAddRow}>
              {t("Add counter")}
            </Button>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
              {showVerifySubmit && (
                <Button
                  variant="outlined"
                  color="success"
                  disabled={bulkLoading}
                  onClick={() => requestSubmit(true)}
                >
                  {t("Submit and verify")}
                </Button>
              )}
              <Button
                variant="contained"
                color="success"
                disabled={bulkLoading}
                onClick={() => requestSubmit()}
              >
                {t("Submit")}
              </Button>
            </Box>
          </Box>
        </Box>
        {lowValueConfirmModal}
      </>
    );
  }

  return (
    <>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        {!hidePreviousCountersTable && (
          <PreviousCountersTable equipmentId={sharedEquipmentId} />
        )}

        <EquipmentCounterRowFields
          formData={formData}
          onChange={handleSingleRowChange}
          hiddenFields={hiddenFields}
          equipmentOptionsOverride={equipmentOptionsOverride}
          fieldErrors={error}
          hideLastValueHint={hideLastValueHint}
          wideFields={wideFields}
        />

        {!hideSubmit && (
          <Box sx={{ display: "flex", width: "100%", justifyContent: "flex-end", gap: 1, flexWrap: "wrap" }}>
            {showVerifySubmit && (
              <Button variant="outlined" color="success" disabled={loading} onClick={() => requestSubmit(true)}>
                {t("Submit and verify")}
              </Button>
            )}
            <Button variant="contained" color="success" disabled={loading} onClick={() => requestSubmit()}>
              {t("Submit")}
            </Button>
          </Box>
        )}
      </Box>
      {lowValueConfirmModal}
    </>
  );
}
